import React, { useState, useEffect, useRef } from "react";
import { Button, Typography, Box, Divider, IconButton } from "@mui/material";
import {
  Close as CloseIcon,
  DateRange as DateRangeIcon,
} from "@mui/icons-material";
import {
  DateSelectionPanel as Panel,
  PanelHeader,
  DatePickerContainer,
  PresetButtonGroup,
  PresetButton,
  DatePickerWrapper,
} from "./styles";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

function DateSelectionPanel({
  isOpen,
  isPinned,
  onClose,
  onTogglePin,
  presetPeriod,
  onPresetChange,
  tempDateRange,
  onStartDateChange,
  onEndDateChange,
  onApply,
}) {
  const panelRef = useRef(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const isClickInsidePanel = (event) => {
    return panelRef.current && panelRef.current.contains(event.target);
  };

  const handlePickerOpen = () => {
    setIsPickerOpen(true);
  };

  const handlePickerClose = () => {
    setIsPickerOpen(false);
  };

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (isPickerOpen || isClickInsidePanel(event)) {
        return;
      }
      if (
        !isClickInsidePanel(event) &&
        !event.target.closest(".date-panel-toggle") &&
        !event.target.closest(".MuiPopover-root")
      ) {
        onClose();
      }
    };

    if (isOpen && !isPinned) {
      document.addEventListener("mousedown", handleDocumentClick, {
        capture: true,
      });
    }

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick, {
        capture: true,
      });
    };
  }, [isOpen, isPinned, isPickerOpen, onClose]);

  return (
    <Panel $isOpen={isOpen} className="date-panel" ref={panelRef}>
      <PanelHeader>
        <Typography variant="subtitle1" fontWeight="bold">
          기간 설정
        </Typography>
        <IconButton size="small" onClick={onClose} title="닫기">
          <CloseIcon fontSize="small" />
        </IconButton>
      </PanelHeader>

      <DatePickerContainer>
        <Typography variant="subtitle2" fontWeight={500} gutterBottom>
          기간 빠른 선택
        </Typography>

        <PresetButtonGroup>
          <PresetButton
            variant={presetPeriod === "1week" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => onPresetChange("1week")}
          >
            최근 1주
          </PresetButton>
          <PresetButton
            variant={presetPeriod === "2weeks" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => onPresetChange("2weeks")}
          >
            최근 2주
          </PresetButton>
          <PresetButton
            variant={presetPeriod === "1month" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => onPresetChange("1month")}
          >
            이번 달
          </PresetButton>
          <PresetButton
            variant={presetPeriod === "3months" ? "contained" : "outlined"}
            color="primary"
            size="small"
            onClick={() => onPresetChange("3months")}
          >
            최근 3개월
          </PresetButton>
        </PresetButtonGroup>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight={500} gutterBottom>
          직접 선택
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePickerWrapper>
            <DatePicker
              label="시작일"
              value={tempDateRange.start}
              onChange={onStartDateChange}
              format="YYYY/MM/DD"
              onOpen={handlePickerOpen}
              onClose={handlePickerClose}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  inputProps: {
                    readOnly: true,
                  },
                },
              }}
            />
            <DatePicker
              label="종료일"
              value={tempDateRange.end}
              onChange={onEndDateChange}
              format="YYYY/MM/DD"
              onOpen={handlePickerOpen}
              onClose={handlePickerClose}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  inputProps: {
                    readOnly: true,
                  },
                },
              }}
            />
          </DatePickerWrapper>
        </LocalizationProvider>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onApply}
            startIcon={<DateRangeIcon />}
          >
            적용하기
          </Button>
        </Box>
      </DatePickerContainer>
    </Panel>
  );
}

export default DateSelectionPanel;
